import type { ReactNode } from "react";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { ChatMessage } from "@/features/intelligence/components/ChatMessage";
import { ThinkingIndicator } from "@/features/intelligence/components/ThinkingIndicator";
import { useThinkingMessage } from "@/features/intelligence/hooks/use-thinking-message";
import { useTranslation } from "@/shared/i18n/LocaleProvider";
import { cn } from "@/shared/cn";
import {
  interruptedClaim,
  isThinking,
  type RunErrorDetail,
  type ThreadNode,
  type ThreadState,
  type ToolActivity,
} from "@/features/intelligence/lib/thread-state";
import { ApprovalPrompt } from "@/features/intelligence/components/ApprovalPrompt";
import { MessageAttachments } from "@/features/intelligence/components/MessageAttachments";
import { ToolCallStrip, type ToolStage } from "@/features/intelligence/components/ToolCallStrip";
import {
  supersededRefusals,
  type FrontendCallRecord,
} from "@/features/intelligence/lib/frontend-tool-outcome";
import { fontSizeVar } from "@/shared/ui/type-scale";

export interface ThreadViewProps {
  thread: ThreadState;
  canReconnect: boolean;
  onReconnect: () => void;
  onDecide: (approved: boolean, reason?: string) => void;
  approvalUnconfirmed: boolean;
  answeringApproval: boolean;
  canRegenerate: boolean;
  onRegenerate: () => void;
  frontendCalls?: ReadonlyMap<string, FrontendCallRecord>;
  belowTool?: (tool: ToolActivity) => ReactNode;
  toolStage?: (tool: ToolActivity) => ToolStage | null;
}

export function ThreadView({
  thread,
  canReconnect,
  onReconnect,
  onDecide,
  approvalUnconfirmed,
  answeringApproval,
  canRegenerate,
  onRegenerate,
  frontendCalls,
  belowTool,
  toolStage,
}: ThreadViewProps) {
  const { t } = useTranslation();
  const thinking = isThinking(thread);
  const phrase = useThinkingMessage(thinking);

  const lastAssistantId =
    [...thread.nodes].reverse().find((n) => n.role === "assistant")?.id ?? null;
  const superseded = supersededRefusals(thread.nodes, frontendCalls);

  return (
    <div className="flex flex-col gap-[16px]">
      {thread.nodes.map((node) => (
        <div key={node.id} className="thread-message-enter">
          <NodeBlock
            node={node}
            conversationId={thread.threadId}
            runError={thread.runError}
            rateLimited={
              node.id === lastAssistantId &&
              thread.outcome === "errored" &&
              thread.runError?.code === RATE_LIMITED
            }
            awaitingApprovalFor={thread.approval?.toolCallId ?? null}
            frontendCalls={frontendCalls}
            superseded={superseded}
            startedAt={thread.toolStartedAt}
            readBack={thread.healedTools}
            belowTool={belowTool}
            toolStage={toolStage}
          />
          {canRegenerate && node.id === lastAssistantId && (
            <button
              type="button"
              onClick={onRegenerate}
              aria-label={t("intelligence.regenerate")}
              title={t("intelligence.regenerate")}
              className={cn(
                "mr-auto mt-[8px] flex h-[28px] w-[28px] items-center justify-center rounded-[var(--radius-md)]",
                "text-[var(--color-text-muted)] transition-colors duration-[var(--duration-fast)]",
                "hover:bg-[var(--color-hover-faint)] hover:text-[var(--color-text)]",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]",
              )}
            >
              <RotateCcw size={14} strokeWidth={2} aria-hidden />
            </button>
          )}
        </div>
      ))}

      {thread.approval && (
        <ApprovalPrompt request={thread.approval} pending={answeringApproval} onDecide={onDecide} />
      )}

      {thinking && <ThinkingIndicator phrase={phrase} />}

      {thread.approvalUnactionable && !thread.approval && (
        <Notice tone="var(--color-rebalance)" message={t("intelligence.approval.unactionable")} />
      )}

      {approvalUnconfirmed && (
        <Notice tone="var(--color-rebalance)" message={t("intelligence.approval.unknown")} />
      )}

      {thread.outcome === "interrupted" &&
        (thread.approval ? (
          <Notice
            tone="var(--color-rebalance)"
            message={t("intelligence.approval.interruptedButAnswerable")}
          />
        ) : (
          <Notice
            tone="var(--color-rebalance)"
            message={t(
              interruptedClaim(thread) === "still-writing"
                ? "intelligence.interruptedStillWriting"
                : "intelligence.interruptedUnknown",
            )}
            action={
              canReconnect
                ? { label: t("intelligence.reconnect"), onClick: onReconnect }
                : undefined
            }
          />
        ))}
    </div>
  );
}

function NodeBlock({
  node,
  conversationId,
  runError,
  rateLimited,
  awaitingApprovalFor,
  frontendCalls,
  superseded,
  startedAt,
  readBack,
  belowTool,
  toolStage,
}: {
  node: ThreadNode;
  conversationId: string | null;
  runError: RunErrorDetail | null;
  rateLimited: boolean;
  awaitingApprovalFor: string | null;
  frontendCalls?: ReadonlyMap<string, FrontendCallRecord>;
  superseded: ReadonlySet<string>;
  startedAt: Readonly<Record<string, number>>;
  readBack: readonly string[];
  belowTool?: (tool: ToolActivity) => ReactNode;
  toolStage?: (tool: ToolActivity) => ToolStage | null;
}) {
  const { t } = useTranslation();
  const receipts = node.tools.filter((tool) => {
    const call = frontendCalls?.get(tool.toolCallId);
    return call?.settled === true && call.ok === true && RECEIPT_TOOLS.includes(tool.toolName);
  });
  const stripped = node.tools.filter(
    (tool) => !receipts.includes(tool) && !superseded.has(tool.toolCallId),
  );
  return (
    <div className="flex flex-col gap-[8px]">
      <ToolCallStrip
        tools={stripped}
        runError={node.errored ? runError : null}
        awaitingApprovalFor={awaitingApprovalFor}
        frontendCalls={frontendCalls}
        startedAt={startedAt}
        readBack={readBack}
        below={belowTool}
        stage={toolStage}
      />
      <MessageAttachments
        attachments={node.attachments}
        conversationId={conversationId}
        align={node.role === "user" ? "end" : "start"}
      />
      {node.text !== "" &&
        (() => {
          const message = (
            <ChatMessage
              message={{
                role: node.role === "user" ? "advisor" : "simon",
                text: node.text,
              }}
            />
          );
          return node.role === "user" ? message : <div>{message}</div>;
        })()}
      {receipts.length > 0 && (
        <div className="flex flex-wrap gap-[6px]">
          {receipts.map((tool) => (
            <span
              key={tool.toolCallId}
              className="inline-flex items-center gap-[8px] rounded-[20px] border border-(--line-strong) bg-(--paper) px-[10px] py-[4px] font-(family-name:--mono) text-xs text-(--muted)"
            >
              ↳ {t(`chatView.receipts.${tool.toolName}`)}
            </span>
          ))}
        </div>
      )}
      {(node.truncated || node.errored) && (
        <div className="flex items-center gap-[8px]">
          <StatePill tone={node.truncated ? "var(--color-rebalance)" : "var(--color-negative)"}>
            {node.truncated ? t("intelligence.truncated") : t("intelligence.errored")}
          </StatePill>
          <span
            className="text-[var(--color-text-muted)]"
            style={{ fontFamily: "var(--font-display)", fontSize: fontSizeVar("sm") }}
          >
            {node.truncated
              ? t("intelligence.truncatedHint")
              : rateLimited
                ? t("intelligence.rateLimitedHint")
                : t("intelligence.erroredHint")}
          </span>
        </div>
      )}
    </div>
  );
}

function StatePill({ tone, children }: { tone: string; children: string }) {
  return (
    <span
      className="inline-flex shrink-0 items-center rounded-[var(--radius-pill)] border px-[7px] py-[1px]"
      style={{
        borderColor: `color-mix(in srgb, ${tone} 40%, transparent)`,
        backgroundColor: `color-mix(in srgb, ${tone} 14%, transparent)`,
      }}
    >
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontWeight: 600,
          fontSize: fontSizeVar("xs"),
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          color: `color-mix(in srgb, ${tone} 55%, var(--color-text-strong))`,
        }}
      >
        {children}
      </span>
    </span>
  );
}

function Notice({
  tone,
  message,
  action,
}: {
  tone: string;
  message: string;
  action?: { label: string; onClick: () => void };
}) {
  return (
    <div
      className="flex flex-wrap items-center gap-[8px] rounded-[var(--radius-md)] border p-[12px]"
      style={{
        borderColor: `color-mix(in srgb, ${tone} 35%, transparent)`,
        backgroundColor: `color-mix(in srgb, ${tone} 10%, transparent)`,
      }}
    >
      <AlertTriangle size={14} aria-hidden style={{ color: tone }} className="shrink-0" />
      <p
        className="min-w-0 flex-1 text-[var(--color-text)]"
        style={{
          fontFamily: "var(--font-display)",
          fontSize: fontSizeVar("sm"),
          lineHeight: "150%",
          letterSpacing: "-0.01em",
        }}
      >
        {message}
      </p>
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          className={cn(
            "flex h-[28px] shrink-0 items-center rounded-[var(--radius-pill)] border px-[12px]",
            "border-[var(--color-border-strong)] text-[var(--color-text-strong)]",
            "transition-colors duration-[var(--duration-fast)] hover:bg-[var(--color-hover-faint)]",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]",
          )}
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 500,
            fontSize: fontSizeVar("sm"),
            letterSpacing: "-0.01em",
          }}
        >
          {action.label}
        </button>
      )}
    </div>
  );
}

const RATE_LIMITED = "llm_rate_limited";

const RECEIPT_TOOLS: readonly string[] = [
  "show_client",
  "close_conversation",
  "start_advisory",
  "open_create_client",
  "show_app_intro",
];
